import { Box, Stack, Typography } from '@mui/material'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Link, useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import AddIcon from '@mui/icons-material/Add'
import { CCEmails } from '../CCEmails'
import { CookiesSetting } from '../CookiesSetting'
import { DidYouKnowRedesign } from '../DidYouKnow'
import { FC } from 'react'
import { InvoicingAddresses } from '../InvoicingAddresses'
import { LanguageSettings } from '../LanguageSettings'
import { MUIButton } from 'components/common/MUIButton'
import { PageContent } from 'components/common/Page/PageContent'
import { Path } from 'constants/router'
import { PersonalInformation } from '../PersonalInformation'
import { SavedPaymentMethods } from '../SavedPaymentMethods'
import { SystemMessage } from 'components/common/SystemMessage'
import { WorkspaceList } from '../WorkspaceList'
import { getPricingUrl } from 'utils/localization'
import { useAuth0 } from 'utils/auth'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceList, useWorkspacePlan } from 'utils/hooks'

export const ProfileSettingsContent: FC = () => {
  const { t, i18n } = useTranslation(['profile', 'invoicing_addresses_profile', 'workspace_profile'])
  const { roles } = useAuth0()
  const navigate = useNavigate()
  const { currentUserWorkspace } = useUserData()
  const { activeWorkspaceList, isWorkspaceResolved } = useWorkspaceList()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const showNoWorkspaceBanner = roles.isClient && isWorkspaceResolved && !currentUserWorkspace


  return (
    <PageContent>
      <Stack gap="5.6rem" padding="5.6rem 0">

        {showNoWorkspaceBanner &&
          <SystemMessage
            variant="info"
            title={t('no_workspace_banner.title')}
            message={
              <Stack gap=".8rem">

                <Typography>
                  <Trans t={t} i18nKey="no_workspace_banner.message">
                    <Link to={Path.WORKSPACE_CREATION}>&nbsp;</Link>
                  </Trans>
                </Typography>

                <Typography>
                  <Trans t={t} i18nKey="no_workspace_banner.upsell">
                    <a href={getPricingUrl(i18n.language)} target="_blank" rel="nofollow noopener noreferrer">&nbsp;</a>
                  </Trans>
                </Typography>

              </Stack>
            }
          />
        }

        <PersonalInformation />

        {!hasWorkspaceActiveSubscription &&
          <DidYouKnowRedesign />
        }

        <LanguageSettings />

        <CCEmails />

        <SavedPaymentMethods />

        {/* Check if the Cookiebot object is properly defined */}
        {/* @ts-ignore */}
        {typeof Cookiebot === 'object' && !!Cookiebot.renew &&
          <CookiesSetting />
        }

        {roles.isClient &&
          <InvoicingAddresses
            sectionTitle={
              <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
                {t('invoicing_addresses_profile:section_title')}
              </Typography>
            }
          />
        }

        {/* WORKSPACE LIST */}
        {roles.isClient &&
          <Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>

              <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
                <Trans t={t} i18nKey="workspaces_title" values={{ count: activeWorkspaceList.length }}>
                  <span style={{ color: GRAY_700 }}>&nbsp;</span>
                </Trans>
              </Typography>

              <MUIButton
                startIcon={<AddIcon />}
                onClick={() => navigate(Path.WORKSPACE_CREATION)}
              >
                {t('workspace_profile:create_new_workspace')}
              </MUIButton>

            </Stack>

            {!!currentUserWorkspace &&
              <Box marginTop={2}>
                <WorkspaceList />
              </Box>
            }

          </Box>
        }

      </Stack>
    </PageContent>
  )
}
